import { isHiddenMetaPath, isKeepPath, type RepositoryFile } from '@trove/core';

export type FileTreeEntry =
  | { type: 'folder'; name: string; path: string }
  | { type: 'file'; name: string; path: string; file: RepositoryFile };

export type FileTreeNode =
  | { type: 'folder'; name: string; path: string; children: FileTreeNode[] }
  | { type: 'file'; name: string; path: string; file: RepositoryFile };

export function fileEntry(file: RepositoryFile): Extract<FileTreeEntry, { type: 'file' }> {
  return {
    type: 'file',
    name: file.path.split('/').at(-1) ?? file.path,
    path: file.path,
    file,
  };
}

export function folderEntry(path: string): Extract<FileTreeEntry, { type: 'folder' }> {
  return {
    type: 'folder',
    name: path.split('/').at(-1) || path || '/',
    path,
  };
}

export function entriesAt(files: RepositoryFile[], folder?: string): FileTreeEntry[] {
  const prefix = folder ? `${folder}/` : '';
  const folders = new Set<string>();
  const entries: FileTreeEntry[] = [];
  for (const file of files) {
    if (isHiddenMetaPath(file.path)) continue;
    if (!file.path.startsWith(prefix)) continue;
    const remaining = file.path.slice(prefix.length);
    const [first, ...rest] = remaining.split('/');
    if (!first || isHiddenMetaPath(first) || first === '.keep') continue;
    if (rest.length) folders.add(first);
    else if (!isKeepPath(file.path))
      entries.push({ type: 'file', name: first, path: file.path, file });
  }
  return [
    ...[...folders]
      .sort()
      .map((name): FileTreeEntry => ({ type: 'folder', name, path: `${prefix}${name}` })),
    ...entries.sort((a, b) => a.name.localeCompare(b.name)),
  ];
}

export function buildFileTree(files: RepositoryFile[], folder?: string): FileTreeNode[] {
  return entriesAt(files, folder).map((entry) => {
    if (entry.type === 'file') return entry;
    return {
      type: 'folder',
      name: entry.name,
      path: entry.path,
      children: buildFileTree(files, entry.path),
    };
  });
}

export function ancestorPaths(path?: string): string[] {
  if (!path) return [];
  const parts = path.split('/');
  return parts.map((_, index) => parts.slice(0, index + 1).join('/'));
}

export function parentFolder(path?: string): string | undefined {
  if (!path) return undefined;
  const parts = path.split('/');
  if (parts.length <= 1) return undefined;
  return parts.slice(0, -1).join('/');
}
